import { component, GbdsElement, prop, OnInit } from '@gbds/core/src/ts/core'
import { animateOpen, animateClose, throttle } from '@gbds/core/src/ts/utils'
import { isShiftTab, isTabOnly } from '@gbds/core/src/ts/utils'
import { getFirstAndLastFocusableElement } from '@gbds/core/src/ts/utils/firstlast-focusable-elements'
import { GlobalNav } from './global-nav'
import { FlyoutService } from '../../utils/flyout-service'
import { lockBodyScroll, unlockBodyScroll } from '../../utils/body-scroll-lock'
import { TabNavEvents } from '@gbds/core/src/ts/components/tab-nav'
import { isMediumViewport, isSmallViewport } from '@gbds/core/src/ts/utils/viewport'
import { isRTL } from '../../../../../gbds-core/src/ts/utils/helper-utils'
import { Overlay } from '../overlay'

enum FlyoutEvents {
	openingComplete = 'gb-openFlyoutOpeningComplete',
	closingComplete = 'gb-openFlyoutClosingComplete',
}

/**
 * @noInheritDoc
 */
@component('gb-flyout')
export class Flyout extends GbdsElement implements OnInit {
	private _backButton: HTMLButtonElement
	private _closeButton: HTMLButtonElement
	private _firstFocusableElement: HTMLElement
	private _flyoutService: FlyoutService
	private _globalNav: GlobalNav
	private _lastFocusableElement: HTMLElement
	private _parentButton: HTMLButtonElement | null
	private _overlay: Overlay
	private _isHamburgerFlyout: boolean

	private _resolver: (value: true) => void

	public loaded = new Promise<boolean>(resolve => {
		this._resolver = resolve
	})

	@prop()
	backButtonLabel: string

	@prop()
	closeButtonLabel: string

	@prop()
	dataHamburgerMenu: boolean

	@prop()
	contentUrl: string

	@prop('boolean')
	slideOnMedium: boolean

	@prop()
	flyoutId: string

	@prop()
	backButtonDtm: string

	@prop()
	backButtonDtm2: string

	constructor() {
		super()
	}

	//
	// Lifecycle
	//

	async onInit(): Promise<void> {
		this._globalNav = document.querySelector('gb-global-nav')
		this._flyoutService = this._globalNav.flyoutService
		this._parentButton = this._globalNav.shadowRoot.querySelector(`button[flyout="${this.flyoutId}"]`)
		this._isHamburgerFlyout = this.dataHamburgerMenu

		if (this.childElementCount !== 0) {
			this._renderStatic()
		} else {
			await this._renderDynamic()
		}

		if (this.slideOnMedium) {
			this._initSlideOnMedium()
		} else {
			this._closeButton = this._createCloseButton()
			this.appendChild(this._closeButton)
		}

		// // The first and last focusable element default to the close button or back button if none other exist
		this._setFirstLastTabbableElements()
		this._addListeners()
		this._addFlyoutToService()

		//for Hamburger flyout types we have to add individiual overlay and not just document level
		//z-index on portable nav and shadow root wont propperly stack unless we use new overlay in flyouts container.
		if (this._isHamburgerFlyout) {
			const flyoutContainer = this._globalNav.shadowRoot.querySelector('.gb-flyouts-container')
			this._overlay = new Overlay(flyoutContainer)
		}

		// TODO: Can we use JS to set the aria attributes? Is that too slow?
		// Screen readers announce navigation content on page load so there's
		// a potential race condition if the JS can't apply the attributes in time
	}

	//
	// Private Methods
	//

	/**
	 *If Hamburger Menu is present. The first and last focusableElement needed to cycle in HB Menu
	 */

	private _setFirstLastTabbableElements() {
		const nestedCompatibleTabNav = this.querySelector('.content-button-group')
		const isHamburgerMenuEnabled = this.getAttribute('data-hamburger-menu')
		// eslint-disable-next-line
		const firstFocusableElementRoot = this
		const lastFocusableElementRoot = this.slideOnMedium && !!nestedCompatibleTabNav ? nestedCompatibleTabNav : this
		const { first } = getFirstAndLastFocusableElement(firstFocusableElementRoot)
		const { last } = getFirstAndLastFocusableElement(lastFocusableElementRoot)

		if (isHamburgerMenuEnabled === 'true') {
			this._firstFocusableElement = first || (this.slideOnMedium ? this._backButton : this._closeButton)
			this._lastFocusableElement = this._closeButton
		} else {
			this._firstFocusableElement = first || (this.slideOnMedium ? this._backButton : this._closeButton)
			this._lastFocusableElement = last || (this.slideOnMedium ? this._backButton : this._closeButton)
		}
	}

	private _setMoreFlyoutAlignment(isMoreFlyoutClosed: boolean) {
		const alignmentRight = 'alignment-right'
		const isOutOfViewport = !isRTL()
			? this.getBoundingClientRect().right > innerWidth
			: this.getBoundingClientRect().left < 0

		if (isMediumViewport() && this.isStackedMenu) {
			if (isMoreFlyoutClosed) {
				this.classList.remove(alignmentRight)
			} else if (isOutOfViewport) {
				this.classList.add(alignmentRight)
			}
		}
	}

	// TODO: Add error handling
	private _renderStatic() {
		const template = this.firstElementChild

		if (template.tagName === 'TEMPLATE') {
			const contents = (template as HTMLTemplateElement).content.cloneNode(true)
			this.appendChild(contents)
			template.remove()
			this._resolveRenderPromise()
		}
	}

	private async _renderDynamic() {
		if (this.contentUrl) {
			const res = await fetch(this.contentUrl)
			const rawHtml = await res.text()
			this.innerHTML = rawHtml
			this._resolveRenderPromise()
		}
	}

	private _resolveRenderPromise() {
		this._resolver(true)
	}

	private _initSlideOnMedium() {
		this.classList.add('slide-medium')

		this._backButton = this._createBackButton()
		this.prepend(this._backButton)
	}

	private _createBackButton(): HTMLButtonElement {
		const button = document.createElement('button')
		button.classList.add('gb-flyout-back', 'stat-text-link')
		button.setAttribute('data-dtm', this.backButtonDtm)
		button.setAttribute('data-dtm2', this.backButtonDtm2)
		button.textContent = this.backButtonLabel
		return button
	}

	private _createCloseButton(): HTMLButtonElement {
		const button = document.createElement('button')
		button.classList.add('gb-flyout-close')
		button.textContent = this.dataHamburgerMenu ? '' : this.closeButtonLabel
		return button
	}

	private _addListeners() {
		this._addClickListeners()
		this._addTabListeners()
		this._addOverlayCloseListener()
		this._addMyaccountListener()
		this._addParentbuttonHoverListener()
	}

	private _addClickListeners() {
		this._addParentButtonClickListener()
		this._addCloseAndBackButtonClickListener()
	}

	private _addParentbuttonHoverListener() {
		const hoverEnabled = this._parentButton?.getAttribute('data-flyout-hover')

		if (!hoverEnabled) {
			return
		}

		this._parentButton?.addEventListener(
			'mouseover',
			throttle(() => {
				if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
					return
				}

				if (this.flyoutOpening) {
					this._addFlyoutOnAnimationEndListener(this.toggleFlyout)
				} else {
					this.toggleFlyout()
				}
			}, 500)
		)

		this.addEventListener(
			'mouseleave',
			throttle(() => {
				if (this.flyoutOpening) {
					this._addFlyoutOnAnimationEndListener(this.toggleFlyout)
				} else {
					this.toggleFlyout()
				}
			}, 500)
		)
	}

	private _addParentButtonClickListener() {
		this._parentButton?.addEventListener(
			'click',
			throttle(() => {
				if (this.flyoutOpening) {
					this._addFlyoutOnAnimationEndListener(this.toggleFlyout)
				} else {
					this.toggleFlyout()
				}
			}, 200)
		)
	}

	private _addCloseAndBackButtonClickListener() {
		this._closeButton?.addEventListener('click', () => {
			this.toggleFlyout()
			this._parentButton?.focus()
		})

		this._backButton?.addEventListener('click', () => {
			this.toggleFlyout()
			this._parentButton?.focus()
		})
	}

	private _addTabListeners() {
		this._parentButton?.addEventListener('keydown', e => {
			this._handleParentButtonTabEvent(e)
			this._handleParentButtonShiftTabEvent(e)
		})

		if (this.slideOnMedium) {
			this._addBackButtonTabListeners()
		} else {
			this._addCloseButtonTabListeners()
		}
	}

	private _handleParentButtonTabEvent(e: KeyboardEvent) {
		/**
		 * Focus the first focusable event when tabbing from the parent button when the flyout is open or opening.
		 */
		if (isTabOnly(e) && (this.flyoutOpen || this.flyoutOpening)) {
			this._firstFocusableElement.focus()
			e.preventDefault()
		}
	}

	private _handleParentButtonShiftTabEvent(e: KeyboardEvent) {
		if (isShiftTab(e) && !this.flyoutOpening && this.flyoutOpen) {
			/**
			 * Close the flyout when the user shift + tabs from the parent button and the flyout is open or not opening.
			 */
			this.toggleFlyout()
		} else if (isShiftTab(e) && this.flyoutOpening) {
			/**
			 * Wait until the flyout opening animation is complete to toggle the flyout when shift + tabbing from the parent button.
			 *
			 * Used for when the user shift + tabs during the flyout opening animation to prevent animation race conditions and
			 * the flyout from accidentally remaining open or toggling again.
			 */
			this._addFlyoutOnAnimationEndListener(this.toggleFlyout)
		}
	}

	private _addBackButtonTabListeners() {
		if (this._backButton) {
			this._backButton.addEventListener('keydown', e => {
				// Don't need to add another listener if the back button IS the first focusable element
				if (this._isFirstFocusableElement(this._backButton)) {
					if (this._isLastFocusableElement(this._backButton)) this._handleCloseOrBackButtonTabEvent(e)
					this._handleFirstFocusableElementShiftTabEvent(e)
				}

				// Go back to the last focusable element when shift tabbing on this button
				if (!this._isLastFocusableElement(this._backButton) && !this.slideOnMedium)
					this._handleBackButtonShiftTab(e)
			})
		}

		// Add another listener if the back button is NOT the first focusable element
		if (!this._isFirstFocusableElement(this._backButton) && this._firstFocusableElement) {
			this._firstFocusableElement.addEventListener('keydown', e =>
				this._handleFirstFocusableElementShiftTabEvent(e)
			)
		}

		// Add another listener if the back button is NOT the last focusable element
		if (!this._isLastFocusableElement(this._backButton) && this._lastFocusableElement) {
			this._lastFocusableElement.addEventListener('keydown', e => this._handleLastFocusableElementTabEvent(e))
		}

		// Add another listener if the back button is NOT the first focusable element
		this._addFirstFocusableElementShiftTabListener(this._backButton)
	}

	private _addCloseButtonTabListeners() {
		if (this._closeButton) {
			this._closeButton.addEventListener('keydown', e => {
				// Don't need to add another listener if the close button IS the first focusable element
				if (this._isFirstFocusableElement(this._closeButton)) this._handleFirstFocusableElementShiftTabEvent(e)

				this._handleCloseOrBackButtonTabEvent(e)
			})
		}

		// Add another listener if the close button is NOT the first focusable element
		this._addFirstFocusableElementShiftTabListener(this._closeButton)
	}

	/**
	 * Adds an event listener to the first focusable element if it's **not** the passed in button
	 * @param button Button element to check if it's the first focusable element or not
	 */
	private _addFirstFocusableElementShiftTabListener(button: HTMLButtonElement) {
		if (!this._isFirstFocusableElement(button) && this._firstFocusableElement) {
			this._firstFocusableElement.addEventListener('keydown', e =>
				this._handleFirstFocusableElementShiftTabEvent(e)
			)
		}
	}

	private _handleBackButtonShiftTab(e: KeyboardEvent) {
		/**
		 * Focus the first focusable event when tabbing from the parent button when the flyout is open or opening.
		 */
		if (isShiftTab(e)) {
			this._lastFocusableElement.focus()

			e.preventDefault()
		}
	}

	/**
	 * Focus the flyout's next sibling (if it exists) and toggle this flyout upon tabbing focus out of the flyout from the close or back button
	 */
	private _handleCloseOrBackButtonTabEvent(e: KeyboardEvent) {
		if (isTabOnly(e)) {
			if (!this.dataHamburgerMenu) {
				this.toggleFlyout()
				this._focusNextVisibleNavElement()
				e.preventDefault()
			} else {
				const firstFocusableElementRoot = this
				const { first } = getFirstAndLastFocusableElement(firstFocusableElementRoot)
				first.focus()
				e.preventDefault()
			}
		}
	}

	private _focusNextVisibleNavElement() {
		if (!this._parentButton) return

		const getMiscNavFirstFocusableElement = () => {
			const miscNav = this._globalNav.shadowRoot.querySelector('.gn-misc')
			const { first } = getFirstAndLastFocusableElement(miscNav)
			return first
		}

		const getMainContentFirstFocusableElement = () => {
			const first = <HTMLElement>this._globalNav.nextElementSibling
			return first
		}

		const findNextVisibleFocusableNavElement = () => {
			const stackedMenuContainer = this._globalNav.shadowRoot.querySelector('.stacked-menu-container')
			let nextSiblingVisible = false
			let nextElementToCheck = this._parentButton?.nextElementSibling as HTMLElement

			while (!nextSiblingVisible) {
				nextSiblingVisible = nextElementToCheck.offsetParent !== null

				// eslint-disable-next-line
				if (!nextSiblingVisible)
					nextElementToCheck = nextElementToCheck.nextElementSibling as HTMLElement
				if (stackedMenuContainer.isSameNode(nextElementToCheck))
					nextElementToCheck = stackedMenuContainer.firstElementChild as HTMLElement
				if (!nextElementToCheck) {
					// There is a custom tab order at Small/Medium Viewports requires user to exit the global nav
					// after traversing Main Navigation bar
					if (isSmallViewport() || isMediumViewport()) {
						nextElementToCheck = getMainContentFirstFocusableElement()
					} else {
						nextElementToCheck = getMiscNavFirstFocusableElement()
					}
				}
			}
			return nextElementToCheck
		}

		if (this.isStackedMenu) {
			// There is a custom tab order at Small/Medium Viewports requires user to exit the global nav
			// after traversing Main Navigation bar
			if (isSmallViewport() || isMediumViewport()) {
				const elToFocus = getMainContentFirstFocusableElement()
				elToFocus.focus()
			} else {
				const elToFocus = getMiscNavFirstFocusableElement()
				elToFocus.focus()
			}
		} else {
			const elToFocus = findNextVisibleFocusableNavElement()
			elToFocus.focus()
		}
	}

	/**
	 * Close this flyout and focus the parent button when shift + tabbing from the first focusable element
	 */
	private _handleFirstFocusableElementShiftTabEvent(e: KeyboardEvent) {
		if (isShiftTab(e)) {
			this.toggleFlyout()
			this._parentButton?.focus()

			e.preventDefault()
		}
	}

	/**
	 * Focus the back button when tabbing from the last focusable element inside the flyout
	 */
	private _handleLastFocusableElementTabEvent(e: KeyboardEvent) {
		if (isTabOnly(e)) {
			this._backButton.focus()

			e.preventDefault()
		}
	}

	private _isFirstFocusableElement(el: HTMLElement) {
		return el.isSameNode(this._firstFocusableElement)
	}

	private _isLastFocusableElement(el: HTMLElement) {
		return el.isSameNode(this._lastFocusableElement)
	}

	/**
	 * Close the flyout when clicking the overlay.
	 */
	private _addOverlayCloseListener() {
		document.addEventListener(
			'gb-overlayClosedFromClick',
			() => {
				if (this.flyoutOpen) {
					this.toggleFlyout(true)
					this._parentButton?.focus()
				} else if (this.flyoutOpening) {
					this._addFlyoutOnAnimationEndListener(this.toggleFlyout, true)
				}
			},
			true
		)
	}

	/**
	 * Close the flyout when Myaccount flyout opens
	 */
	private _addMyaccountListener() {
		const modalEvents = ['gbpe-myAccountFlyout-open', 'gbpe-modal-open']
		modalEvents.forEach(modalEvent =>
			document.addEventListener(modalEvent, () => {
				if (this.flyoutOpen) {
					this.toggleFlyout()
				} else if (this.flyoutOpening) {
					this._addFlyoutOnAnimationEndListener(this.toggleFlyout)
				}
			})
		)
	}

	/**
	 * Wait for an animation to end on this flyout before firing the callback.
	 */
	/* eslint-disable-next-line */
	private _addFlyoutOnAnimationEndListener(callback: () => void, ...args: any[]) {
		this.addEventListener(
			'animationend',
			() => {
				callback.call(this, args)
			},
			{
				once: true,
			}
		)
	}

	private _addFlyoutToService() {
		this._flyoutService.addFlyout(this, this.loaded)
	}

	private _toggleAriaExpanded(button: HTMLButtonElement) {
		button.getAttribute('aria-expanded') === 'true'
			? button.setAttribute('aria-expanded', 'false')
			: button.setAttribute('aria-expanded', 'true')
	}

	private _toggleAriaHidden() {
		this.getAttribute('aria-hidden') === 'true'
			? this.setAttribute('aria-hidden', 'false')
			: this.setAttribute('aria-hidden', 'true')
	}

	private _openRegularFlyout(
		triggeredByOverlayClosed: boolean,
		triggeredByCloseOpenFlyout: boolean,
		triggeredByEscape: boolean
	) {
		const closedFlyoutId = this._flyoutService.closeOpenFlyout()
		if (closedFlyoutId !== this.flyoutId) {
			if (this.isStackedMenu && !isSmallViewport()) this.style.height = this._getStackedMenuHeightStyle()
			// Override `triggeredByCloseOpenFlyout` when an open flyout was closed by the service; we want
			// to keep the overlay open while opening the new flyout.
			this._openAndToggleOverflow(
				triggeredByOverlayClosed,
				closedFlyoutId ? true : triggeredByCloseOpenFlyout,
				triggeredByEscape
			)
			this._setFirstLastTabbableElements()
			this._setMoreFlyoutAlignment(false)
		}
	}

	private _openSlideOnMediumFlyout(
		triggeredByOverlayClosed: boolean,
		triggeredByCloseOpenFlyout: boolean,
		triggeredByEscape: boolean
	) {
		const { flyoutHeight, navHeight } = this.flyoutAndNavHeight
		this.parentElement.style.height = this._getStackedMenuHeightStyle()
		// Prevent scrolling when the second level flyout is smaller than the height of the window
		// This is to prevent someone from scrolling past the contents of the second level flyout and seeing the stacked menu contents
		if (flyoutHeight + navHeight <= window.innerHeight) {
			this.parentElement.style.overflowY = 'hidden'
			const nestedTabNav = this.querySelector('gb-tab-nav')
			nestedTabNav?.addEventListener(TabNavEvents.stackedTabOpened, (e: CustomEvent) => {
				const yPosition = this.getBoundingClientRect().y.toString() + 'px'

				this.parentElement.style.overflowY = ''
				e.detail.openContainer.style.top = yPosition
				e.detail.openContainer.style.height = 'calc(100% - ' + yPosition + ')'
			})
			nestedTabNav?.addEventListener(
				TabNavEvents.stackedTabClosed,
				() => (this.parentElement.style.overflowY = 'hidden')
			)
		}
		this._openAndToggleOverflow(triggeredByOverlayClosed, triggeredByCloseOpenFlyout, triggeredByEscape)
		this._setFirstLastTabbableElements()
		this._backButton.focus()
	}

	private _getStackedMenuHeightStyle() {
		const { flyoutHeight, navHeight } = this.flyoutAndNavHeight
		return `min(calc(100vh - ${navHeight}px), ${flyoutHeight}px)`
	}

	private _openAndToggleOverflow(
		triggeredByOverlayClosed: boolean,
		triggeredByCloseOpenFlyout: boolean,
		triggeredByEscape: boolean
	) {
		lockBodyScroll()
		document.documentElement.style.setProperty('--nav-y', `${this._globalNav.getBoundingClientRect().y}px`)
		this._toggleOverlay(triggeredByOverlayClosed, triggeredByCloseOpenFlyout, triggeredByEscape)
		animateOpen(this, () => document.dispatchEvent(new CustomEvent(FlyoutEvents.openingComplete)))
	}

	private _closeFlyout(
		triggeredByCloseOpenFlyout: boolean,
		triggeredByOverlayClosed: boolean,
		triggeredByEscape: boolean
	) {
		const disclosureOpen = document.querySelector('.gb-disclosure-open')
		if (!disclosureOpen) {
			lockBodyScroll()
			this._toggleOverlay(triggeredByOverlayClosed, triggeredByCloseOpenFlyout, triggeredByEscape)

			animateClose(this, () => {
				// Close a second level flyout if one is open
				if (this.isStackedMenu && this._flyoutService.flyoutOpen) {
					this._flyoutService.closeOpenFlyout()
				}

				const { flyoutHeight, navHeight } = this.flyoutAndNavHeight

				if (flyoutHeight + navHeight <= window.innerHeight) {
					const nestedTabNav = this.querySelector('gb-tab-nav')
					nestedTabNav?.removeEventListener(TabNavEvents.stackedTabOpened, (e: CustomEvent) => {
						const yPosition = this.getBoundingClientRect().y.toString() + 'px'

						this.parentElement.style.overflowY = ''
						e.detail.openContainer.style.top = yPosition
						e.detail.openContainer.style.height = 'calc(100% - ' + yPosition + ')'
					})
					nestedTabNav?.removeEventListener(
						TabNavEvents.stackedTabClosed,
						() => (this.parentElement.style.overflowY = 'hidden')
					)
				}
				/**
				 * Tab Nav content container and the flyout both use visibility: visible;
				 * This is causing the content container to stay visible after the flyout is closed,
				 * and also keeps you from opening the More... flyout.
				 * Adding this code to resolve this issue. Will see if it makes more sense for the flyout to
				 * use display: none; so this is not an issue anymore.
				 */
				if (this.querySelectorAll('gb-tab-nav .content-container.open').length > 0) {
					this.querySelectorAll('gb-tab-nav .content-container.open').forEach(cnt =>
						cnt.classList.remove('open')
					)
				}
				const myAccountMenu = this._globalNav.shadowRoot.querySelector('gb-myaccount-nav')
				const myAccountMenuOpen = myAccountMenu ? myAccountMenu.getAttribute('flyout') === 'open' : false
				// Don't reset body overflow when we're closing a second level flyout and when closing from escape key press
				if (triggeredByEscape || (!triggeredByCloseOpenFlyout && !this.slideOnMedium && !myAccountMenuOpen)) {
					unlockBodyScroll()
				} else if (!triggeredByCloseOpenFlyout) {
					this._parentButton?.focus()
				}
				if (this.slideOnMedium) {
					this.parentElement.style.overflowY = ''
					this.parentElement.style.height = ''
				}
				if (this.isStackedMenu) this.style.height = ''
				document.dispatchEvent(new CustomEvent(FlyoutEvents.closingComplete))
				this._setMoreFlyoutAlignment(true)
				//resets hamburgerMenuFlyout visibility because when stacked menu is active it needed to hide to prevent ios positioning  bugs
				//Also the close icon is inside a fixed positioned box while also being fixed positioned. needs specific positioning
				if (this.dataHamburgerMenu) {
					this.querySelector('.gb-flyout-close').classList.remove('gb-in-stacked-hb-close')
					this.style.visibility = ''
				}
			})
		} else if (!this._isHamburgerFlyout) {
			window.gbpe.overlay.toggleOverlay()
		}
	}

	private _toggleOverlay(
		triggeredByOverlayClosed: boolean | [],
		triggeredByCloseOpenFlyout: boolean,
		triggeredByEscape: boolean
	) {
		// For whatever horrible, inexplicable reason, `triggeredByOverlayClosed` can sometimes be an empty array???
		// So that's why I do this:
		const triggeredByOverlayClosedBool =
			typeof triggeredByOverlayClosed === 'object'
				? triggeredByOverlayClosed.length > 0
				: triggeredByOverlayClosed
		const overlay = window.gbpe.overlay

		/*
			Gotta do this because the overlay doesn't automatically toggle when
			a flyout was closed by the service.

			The service isn't aware of the overlay because the service is only responsible
			for the orchestration of closing flyouts.

			Keep overlay open when sliding in a second level flyout.
		*/
		if (
			triggeredByEscape ||
			(!triggeredByOverlayClosedBool && !this.slideOnMedium && !triggeredByCloseOpenFlyout)
		) {
			if (overlay.overlayAnimating) {
				overlay.addOverlayFinishAnimatingCallback(() => overlay.toggleOverlay())
			} else if (!this._isHamburgerFlyout) {
				overlay.toggleOverlay()
			}
		}

		if (
			this._isHamburgerFlyout &&
			!triggeredByEscape &&
			!triggeredByOverlayClosedBool &&
			triggeredByCloseOpenFlyout
		) {
			overlay.toggleOverlay()
		}
	}

	//
	// Public Methods
	//

	/**
	 * Toggles the visibility of the flyout.
	 * @param triggeredByOverlayClosed Used to determine if the overlay needs to be closed. Not likely to be used outside of the `FlyoutService`
	 * @param triggeredByCloseOpenFlyout Used to determine if the body overflow needs to stay locked. Not likely to be used outside of the `FlyoutService`
	 */
	public toggleFlyout(
		triggeredByOverlayClosed = false,
		triggeredByCloseOpenFlyout = false,
		triggeredByEscape = false
	): void {
		const openSlideOrRegularFlyout = () => {
			if (this.slideOnMedium) {
				// Make the container the right height b/c the contents doesn't impact the document flow as they're absolutely positioned
				this._openSlideOnMediumFlyout(triggeredByOverlayClosed, triggeredByCloseOpenFlyout, triggeredByEscape)
			} else {
				// Close the flyout that's open (if there is one) before opening this flyout
				this._openRegularFlyout(triggeredByOverlayClosed, triggeredByCloseOpenFlyout, triggeredByEscape)
			}
		}

		window.gbpe.overlay.setZHeight(122)

		if (this.flyoutOpen) {
			this._closeFlyout(triggeredByCloseOpenFlyout, triggeredByOverlayClosed, triggeredByEscape)
		} else if (this._flyoutService.flyoutOpening) {
			document.addEventListener(FlyoutEvents.openingComplete, openSlideOrRegularFlyout, { once: true })
		} else {
			openSlideOrRegularFlyout()
		}

		// Focus the containing flyout's parent button when this slide on medium flyout is closed by the overlay
		// If we didn't do this then focus would be lost on a non-visible element
		if (triggeredByOverlayClosed && this.slideOnMedium) {
			const containingFlyout = this.parentElement as Flyout
			containingFlyout.flyoutParentButton?.focus()
		}

		if (this._parentButton) {
			this._toggleAriaExpanded(this._parentButton)
		}
		this._toggleAriaHidden()

		if (this._isHamburgerFlyout) {
			if (!triggeredByOverlayClosed) {
				this._overlay.toggleOverlay()
			}
		}
	}

	private get flyoutAndNavHeight() {
		const flyoutHeight = this.getBoundingClientRect().height
		const navHeight = this._globalNav.getBoundingClientRect().height
		return { flyoutHeight, navHeight }
	}

	private get isStackedMenu() {
		return this.classList.contains('stacked-flyout')
	}

	//
	// Public Accessors
	//

	/**
	 * Returns true if the flyout is closing, overwise false.
	 */
	public get flyoutClosing(): boolean {
		return this.classList.contains('closing')
	}

	/**
	 * Returns true if the flyout is open, overwise false.
	 */
	public get flyoutOpen(): boolean {
		return this.classList.contains('open')
	}

	/**
	 * Returns true if the flyout is opening, overwise false.
	 */
	public get flyoutOpening(): boolean {
		return this.classList.contains('opening')
	}

	/**
	 * Returns the flyouts parent button.
	 */
	public get flyoutParentButton(): HTMLButtonElement | null {
		return this._parentButton
	}
}
